import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  linksDb,
  questionsDb,
  tipsDb,
  notesDb,
  appsDb,
} from '../lib/db/index.js';
import { searchByFields } from '../lib/utils/search.js';
import { PageHeader } from '../components/shared/PageHeader.js';
import { FilterBar } from '../components/shared/FilterBar.js';
import { EmptyState } from '../components/shared/EmptyState.js';
import { Spinner } from '../components/shared/Spinner.js';
import { LinkRow } from '../components/rows/LinkRow.js';
import { QARow } from '../components/rows/QARow.js';
import { TipRow } from '../components/rows/TipRow.js';
import { NoteRow } from '../components/rows/NoteRow.js';
import { AppRow } from '../components/rows/AppRow.js';

type Tab = 'all' | 'link' | 'qa' | 'tip' | 'note' | 'app';

const MAX_QUERY_LENGTH = 100;

function normalizeQuery(raw: string | null | undefined): string {
  return (raw ?? '').trim().slice(0, MAX_QUERY_LENGTH);
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = normalizeQuery(params.get('q'));
  const [tab, setTab] = useState<Tab>('all');
  // URL の q が他経路（ブラウザの戻る/進む等）で変わった時に input を追従させる
  const [draft, setDraft] = useState(q);
  useEffect(() => {
    setDraft(q);
  }, [q]);

  const enabled = q.length > 0;

  const linksQ = useQuery({
    queryKey: ['links', 'shared'],
    queryFn: () => linksDb.findShared({ count: 200 }),
    enabled,
  });
  const qaQ = useQuery({
    queryKey: ['questions', 'shared'],
    queryFn: () => questionsDb.findShared({ count: 200 }),
    enabled,
  });
  const tipsQ = useQuery({
    queryKey: ['tips', 'shared'],
    queryFn: () => tipsDb.findShared({ count: 200 }),
    enabled,
  });
  const notesQ = useQuery({
    queryKey: ['notes', 'shared'],
    queryFn: () => notesDb.findShared({ count: 200 }),
    enabled,
  });
  const appsQ = useQuery({
    queryKey: ['apps', 'shared'],
    queryFn: () => appsDb.findShared({ count: 200 }),
    enabled,
  });

  const linkHits = useMemo(
    () => searchByFields(linksQ.data ?? [], q, ['title', 'summary', 'userComment', 'channelName', 'domain', 'sourceType', 'tags']),
    [linksQ.data, q],
  );
  const qaHits = useMemo(
    () => searchByFields(qaQ.data ?? [], q, ['title', 'body', 'tags']),
    [qaQ.data, q],
  );
  const tipHits = useMemo(
    () => searchByFields(tipsQ.data ?? [], q, ['title', 'body', 'tags']),
    [tipsQ.data, q],
  );
  const noteHits = useMemo(
    () => searchByFields(notesQ.data ?? [], q, ['title', 'purpose', 'result', 'conclusion', 'tags']),
    [notesQ.data, q],
  );
  const appHits = useMemo(
    () => searchByFields(appsQ.data ?? [], q, ['name', 'summary', 'purpose', 'tags']),
    [appsQ.data, q],
  );

  const allQueries = [linksQ, qaQ, tipsQ, notesQ, appsQ];
  const isPending = enabled && allQueries.some((qry) => qry.isPending);
  const isFetching = enabled && allQueries.some((qry) => qry.isFetching);
  const errorCount = allQueries.filter((qry) => qry.isError).length;

  const totalHits =
    linkHits.length + qaHits.length + tipHits.length + noteHits.length + appHits.length;

  const filters: { key: Tab; label: string; count: number }[] = [
    { key: 'all', label: 'すべて', count: totalHits },
    { key: 'link', label: 'URL共有', count: linkHits.length },
    { key: 'qa', label: 'Q&A', count: qaHits.length },
    { key: 'tip', label: 'TIPS', count: tipHits.length },
    { key: 'note', label: '検証メモ', count: noteHits.length },
    { key: 'app', label: '作成アプリ', count: appHits.length },
  ];

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = normalizeQuery(draft);
    if (!trimmed) return;
    setParams({ q: trimmed });
    setTab('all');
  };

  const show = (t: Tab) => tab === 'all' || tab === t;
  const visibleCount =
    (show('link') ? linkHits.length : 0) +
    (show('qa') ? qaHits.length : 0) +
    (show('tip') ? tipHits.length : 0) +
    (show('note') ? noteHits.length : 0) +
    (show('app') ? appHits.length : 0);

  return (
    <div className="page">
      <PageHeader
        eyebrow="SEARCH"
        title="横断検索"
        subtitle="URL共有 / Q&A / TIPS / 検証メモ / 作成アプリ を一度に検索します（共有可視のもののみ）。"
      />

      <div className="search-back">
        <Link to="/" className="btn ghost xs">← ホームに戻る</Link>
      </div>

      <form onSubmit={onSubmit} role="search" className="search-input-wrap">
        <input
          type="search"
          placeholder={`キーワードを入力して Enter（スペース区切りで AND、最大 ${MAX_QUERY_LENGTH} 文字）`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="横断検索キーワード"
          className="list-search"
          maxLength={MAX_QUERY_LENGTH}
          autoFocus={q === ''}
        />
      </form>

      {!enabled && <EmptyState title="キーワードを入力してください" />}

      {enabled && (
        <>
          <div className="search-filter">
            <FilterBar<Tab>
              filters={filters}
              value={tab}
              onChange={setTab}
              groupLabel="種別"
            />
          </div>

          {errorCount > 0 && (
            <div className="search-error" role="alert">
              ⚠️ 一部のコレクションの検索に失敗しました（{errorCount} 件）。時間をおいて再読み込みしてください。
            </div>
          )}

          {isPending && <Spinner />}

          {!isPending && isFetching && (
            <div className="search-refetching" aria-live="polite">
              最新の結果を取得しています…
            </div>
          )}

          {!isPending && totalHits === 0 && errorCount === 0 && (
            <EmptyState
              title="該当する結果はありません"
              description={`「${q}」にヒットするものが見つかりませんでした。`}
            />
          )}

          {!isPending && totalHits > 0 && visibleCount === 0 && (
            <EmptyState
              title="このタブには結果がありません"
              description="種別タブで「すべて」または別のタブをご覧ください。"
            />
          )}

          {!isPending && visibleCount > 0 && (
            <div className="search-results">
              {show('link') && linkHits.length > 0 && (
                <section className="search-section">
                  <h2 className="search-section-title">URL共有 ({linkHits.length})</h2>
                  {linkHits.map((l) => (
                    <LinkRow key={l.id} link={l} />
                  ))}
                </section>
              )}
              {show('qa') && qaHits.length > 0 && (
                <section className="search-section">
                  <h2 className="search-section-title">Q&A ({qaHits.length})</h2>
                  {qaHits.map((qq) => (
                    <QARow key={qq.id} q={qq} />
                  ))}
                </section>
              )}
              {show('tip') && tipHits.length > 0 && (
                <section className="search-section">
                  <h2 className="search-section-title">TIPS ({tipHits.length})</h2>
                  {tipHits.map((t) => (
                    <TipRow key={t.id} tip={t} />
                  ))}
                </section>
              )}
              {show('note') && noteHits.length > 0 && (
                <section className="search-section">
                  <h2 className="search-section-title">検証メモ ({noteHits.length})</h2>
                  {noteHits.map((n) => (
                    <NoteRow key={n.id} note={n} />
                  ))}
                </section>
              )}
              {show('app') && appHits.length > 0 && (
                <section className="search-section">
                  <h2 className="search-section-title">作成アプリ ({appHits.length})</h2>
                  {appHits.map((a) => (
                    <AppRow key={a.id} app={a} />
                  ))}
                </section>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
